import { useState, type FormEventHandler as SubmitEventHandler } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { AuthPageLayout } from "../components/layout/PageLayouts";
import { useAuth } from "../lib/auth-context";

export default function RegisterPage() {
	const { register } = useAuth();
	const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001").replace(/\/+$/, "");
	const location = useLocation();
	const routeState = location.state as { prefillEmail?: string } | null;
	const initialEmail = routeState?.prefillEmail?.trim() || "";
	const [email, setEmail] = useState(initialEmail);
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);
	const [loading, setLoading] = useState(false);

	const onSubmit: SubmitEventHandler<HTMLFormElement> = async (e) => {
		e.preventDefault();
		setError("");
		if (password !== confirm) {
			setError("Passwords do not match.");
			return;
		}
		if (!acceptedTerms) {
			setError("You must agree to the Terms of Use and Privacy Policy.");
			return;
		}
		setLoading(true);
		try {
			await register(email, password);
			setDone(true);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Registration failed");
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout>
			<Card>
				<CardHeader>
					<CardTitle>Register</CardTitle>
				</CardHeader>
				<CardContent>
					{done ? (
						<div className="space-y-5 text-center">
							<p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700">
								We sent a confirmation link to your email. Please confirm your email to continue.
							</p>
							<hr />
							<div className="space-y-2">
								<p className="text-sm text-slate-600 font-medium">Didn&apos;t get it?</p>
								<Button asChild variant="outline" className="w-full">
									<NavLink
										to="/resend-confirmation"
										state={email.trim() ? { prefillEmail: email.trim() } : undefined}
									>
										Resend confirmation email
									</NavLink>
								</Button>
							</div>
							<hr />
							<div className="space-y-2">
								<Button asChild className="w-full">
									<NavLink
										to="/login"
										state={email.trim() ? { prefillEmail: email.trim() } : undefined}
									>
										Login
									</NavLink>
								</Button>
							</div>
						</div>
					) : (
						<>
							<Button asChild variant="outline" className="mb-4 w-full">
								<a
									href={`${API_URL}/auth/google/start?returnTo=${encodeURIComponent("/dashboard")}`}
									onClick={() => {
										if (typeof window === "undefined") return;
										window.sessionStorage.setItem("oauth:google:pending", "1");
									}}
								>
									<svg aria-hidden="true" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
										<path
											fill="#4285F4"
											d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h6.45a5.52 5.52 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.56-5.16 3.56-8.66z"
										/>
										<path
											fill="#34A853"
											d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.88-3A7.2 7.2 0 0 1 12 19.4a7.2 7.2 0 0 1-6.77-4.98H1.22v3.12A12 12 0 0 0 12 24z"
										/>
										<path
											fill="#FBBC05"
											d="M5.23 14.42A7.2 7.2 0 0 1 4.83 12c0-.84.14-1.66.4-2.42V6.46H1.22A12 12 0 0 0 0 12c0 1.94.46 3.78 1.22 5.54l4-3.12z"
										/>
										<path
											fill="#EA4335"
											d="M12 4.6c1.76 0 3.34.6 4.58 1.78l3.43-3.43C17.95 1.07 15.24 0 12 0A12 12 0 0 0 1.22 6.46l4.01 3.12A7.2 7.2 0 0 1 12 4.6z"
										/>
									</svg>
									Continue with Google
								</a>
							</Button>
							<form className="space-y-4" onSubmit={onSubmit}>
								<label className="block text-sm font-semibold text-slate-700">
									Email
									<input
										type="email"
										className="mt-2 w-full rounded-md border border-slate-200 p-2"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										autoComplete="email"
										required
									/>
								</label>
								<label className="block text-sm font-semibold text-slate-700">
									Password
									<input
										type="password"
										className="mt-2 w-full rounded-md border border-slate-200 p-2"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										autoComplete="new-password"
										required
										minLength={8}
									/>
								</label>
								<label className="block text-sm font-semibold text-slate-700">
									Confirm password
									<input
										type="password"
										className="mt-2 w-full rounded-md border border-slate-200 p-2"
										value={confirm}
										onChange={(e) => setConfirm(e.target.value)}
										autoComplete="new-password"
										required
										minLength={8}
									/>
								</label>
								<label className="flex items-start gap-2 text-sm text-slate-700">
									<Checkbox
										checked={acceptedTerms}
										onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
										className="mt-1"
										aria-label="Agree to Terms of Use and Privacy Policy"
									/>
									<span className="leading-6">
										I agree to the{" "}
										<Link to="/terms" className="underline">
											Terms of Use
										</Link>{" "}
										and{" "}
										<Link to="/privacy" className="underline">
											Privacy Policy
										</Link>
										.
									</span>
								</label>
								{error && <p className="rounded-md bg-red-50 p-2 text-sm text-red-600">{error}</p>}
								<Button type="submit" disabled={loading} className="w-full">
									{loading ? "Creating..." : "Create account"}
								</Button>
							</form>
							<p className="mt-4 text-sm text-slate-600">
								Already have an account?{" "}
								<NavLink
									to="/login"
									state={email.trim() ? { prefillEmail: email.trim() } : undefined}
									className="font-semibold text-ink"
								>
									Login
								</NavLink>
							</p>
						</>
					)}
				</CardContent>
			</Card>
		</AuthPageLayout>
	);
}
